import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Users, Car, FileText, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenAhrensburgPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-ahrensburg-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wie schnell bekomme ich einen Termin in Ahrensburg?",
      a: "In Ahrensburg und dem Kreis Stormarn vergeben wir Termine meist innerhalb von 24-48 Stunden. Bei dringenden Fällen sind wir oft noch am selben Tag bei Ihnen. Die Fahrzeit von unserem Hauptsitz in Rellingen nach Ahrensburg beträgt etwa 35-40 Minuten über die A1, sodass wir flexibel und schnell reagieren können."
    },
    {
      q: "Wer bezahlt das Gutachten bei einem Unfall in Ahrensburg?",
      a: "Bei einem unverschuldeten Unfall übernimmt die gegnerische Haftpflichtversicherung die Kosten für das Gutachten vollständig. Sie haben das gesetzliche Recht auf freie Gutachterwahl – lassen Sie sich nicht von der Versicherung zu einem ihrer Gutachter drängen. Ein unabhängiger Gutachter arbeitet in Ihrem Interesse, nicht im Interesse der Versicherung."
    },
    {
      q: "Welche Orte im Kreis Stormarn deckt ihr ab?",
      a: "Wir betreuen den gesamten Kreis Stormarn: Ahrensburg, Bargteheide, Bad Oldesloe, Reinbek, Glinde, Trittau, Großhansdorf, Ammersbek, Stapelfeld, Siek, Barsbüttel, Oststeinbek, Braak und alle weiteren Gemeinden. Auch die angrenzenden Hamburger Stadtteile wie Wandsbek und Rahlstedt gehören zu unserem Einsatzgebiet."
    },
    {
      q: "Was ist bei einem Unfall auf der A1 bei Ahrensburg zu beachten?",
      a: "Unfälle auf der A1 zwischen dem Kreuz Hamburg-Ost und Bad Oldesloe kommen leider häufig vor. Nach einem Autobahnunfall dokumentieren Sie zunächst die Unfallstelle mit Fotos, bevor die Fahrzeuge bewegt werden. Ihr Fahrzeug wird dann meist abgeschleppt – wir kommen zur Werkstatt oder zum Abstellort und begutachten dort. Rufen Sie uns möglichst früh an, damit wir den Prozess beschleunigen können."
    },
    {
      q: "Brauche ich bei einem Bagatellschaden auch ein Gutachten?",
      a: "Bei Schäden unter 750€ lohnt sich oft nur ein Kostenvoranschlag. Ab etwa 750-1.000€ Schadenshöhe empfehlen wir ein vollständiges Gutachten, da hier zusätzlich Wertminderung und Nutzungsausfall geltend gemacht werden können. Wir beraten Sie gerne telefonisch, ob sich ein Gutachten in Ihrem Fall lohnt – kostenlos und unverbindlich."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Ahrensburg | Kfz-Sachverständige Stormarn | Dein Gutachten Direkt</title>
        <meta name="description" content="Unfallgutachten in Ahrensburg und Kreis Stormarn. Über 10 Jahre Erfahrung, gerichtsfeste Gutachten. Termine in 24-48h. Kostenlos bei unverschuldetem Unfall. ☎ Jetzt anrufen!" />
        <meta name="keywords" content="Unfallgutachten Ahrensburg, Kfz Gutachter Ahrensburg, Sachverständiger Stormarn, Gutachter Bargteheide, Gutachter Bad Oldesloe, Gutachter Reinbek, Kreis Stormarn" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-ahrensburg" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Ahrensburg & Stormarn",
            "description": "Kfz-Sachverständigenbüro mit über 10 Jahren Erfahrung. Unabhängige Unfallgutachten in Ahrensburg, Bargteheide, Bad Oldesloe und dem gesamten Kreis Stormarn.",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Ahrensburg",
              "addressRegion": "Schleswig-Holstein",
              "addressCountry": "DE"
            },
            "areaServed": [
              {"@type": "City", "name": "Ahrensburg"},
              {"@type": "City", "name": "Bargteheide"},
              {"@type": "City", "name": "Bad Oldesloe"},
              {"@type": "City", "name": "Reinbek"},
              {"@type": "City", "name": "Glinde"},
              {"@type": "City", "name": "Trittau"},
              {"@type": "City", "name": "Großhansdorf"}
            ],
            "priceRange": "€€"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Ahrensburg & Kreis Stormarn</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Ahrensburg</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Unabhängige Kfz-Sachverständige mit <strong className="text-white">über 10 Jahren Erfahrung</strong> im Kreis Stormarn. 
                  Von Ahrensburg bis Bad Oldesloe – wir erstellen gerichtsfeste Gutachten direkt vor Ort.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gesamter Kreis Stormarn</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Zusammenarbeit mit Anwälten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Termine in 24-48h</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Kostenlose Erstberatung</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Einsatzgebiet Stormarn</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Abgedeckte Gebiete</p>
                      <p className="text-gray-400">Gesamter Kreis Stormarn</p>
                      <p className="text-gray-400">Ahrensburg, Bargteheide, Bad Oldesloe, Reinbek</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Anfahrt</p>
                      <p className="text-gray-400">35-40 Min. von Rellingen (über A1)</p>
                      <p className="text-gray-400">Termine meist in 24-48 Stunden</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Unser Team</p>
                      <p className="text-gray-400">Mehrere erfahrene Sachverständige</p>
                      <p className="text-gray-400">Hauptsitz in Rellingen (Kreis Pinneberg)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter selbst wählen. Die gegnerische Versicherung muss die Kosten übernehmen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ihr Kfz-Gutachter für Ahrensburg & Stormarn</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist Ihr unabhängiger Kfz-Sachverständiger für Ahrensburg und den gesamten Kreis Stormarn. 
                Mit unserem Hauptsitz in <strong>Rellingen</strong> sind wir über die A1 in etwa 35-40 Minuten in Ahrensburg – 
                schnell genug, um Ihnen zeitnah zu helfen.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Seit über <strong>10 Jahren</strong> erstellen wir unabhängige Unfallgutachten für Geschädigte in Stormarn. 
                Ob Auffahrunfall in der Ahrensburger Innenstadt, Parkrempler auf dem City-Center-Parkplatz oder 
                Wildunfall auf dem Weg nach Bargteheide – wir dokumentieren jeden Schaden <strong>gerichtsfest</strong>.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Wir kennen den Kreis Stormarn gut und haben über die Jahre ein Netzwerk aufgebaut: 
                Wir wissen, welche Werkstätten in der Region zuverlässig arbeiten, und arbeiten eng mit 
                <strong> Fachanwälten für Verkehrsrecht</strong> in Hamburg und Stormarn zusammen.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen oder Werkstätten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung in der Kfz-Schadensbewertung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Anwaltsnetzwerk:</strong> Enge Zusammenarbeit mit Fachanwälten in Hamburg und Stormarn</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Schnell:</strong> Termine in Stormarn meist innerhalb 24-48 Stunden</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Alle Ansprüche dokumentieren – nicht nur die Reparaturkosten</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Reparaturkostenkalkulation</h3>
                </div>
                <p className="text-gray-700">Detaillierte Berechnung nach Herstellervorgaben: Arbeitslohn, Original-Ersatzteile, Lackierung, Achsvermessung und alle Nebenkosten wie Verbringungskosten.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Car className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wertminderung</h3>
                </div>
                <p className="text-gray-700">Der merkantile Minderwert Ihres Fahrzeugs nach dem Unfall. Bei einem 3 Jahre alten Auto mit Unfallschaden oft 300-800€ – auch nach perfekter Reparatur.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Nutzungsausfall</h3>
                </div>
                <p className="text-gray-700">Entschädigung für jeden Tag ohne Ihr Fahrzeug. Je nach Fahrzeugklasse 23€ bis 175€ täglich. Bei 10 Tagen Werkstattzeit: 350-500€ zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wiederbeschaffungswert</h3>
                </div>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Ersatzfahrzeug? Wir recherchieren aktuelle Angebote im lokalen und regionalen Markt.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Restwertermittlung</h3>
                </div>
                <p className="text-gray-700">Der Verkaufswert Ihres beschädigten Fahrzeugs bei Totalschaden. Wir holen mehrere Angebote ein, damit Sie den besten Preis erzielen.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Fotodokumentation</h3>
                </div>
                <p className="text-gray-700">30-50 professionelle Fotos aller Schäden aus verschiedenen Winkeln. Diese Beweissicherung ist im Streitfall vor Gericht entscheidend.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Wichtig:</strong> Ein Werkstatt-Kostenvoranschlag dokumentiert nur Reparaturkosten. 
                Wertminderung und Nutzungsausfall fehlen – das kann bei einem Mittelklassewagen leicht 500-1.500€ sein!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen zu Unfallgutachten in Ahrensburg</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stadtteile */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Kreis Stormarn & Hamburg-Ost</h2>
            <p className="text-lg text-gray-600 mb-8">Alle Gemeinden in Stormarn und die angrenzenden Hamburger Stadtteile</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Ahrensburg', 'Bargteheide', 'Bad Oldesloe', 'Reinbek', 'Glinde', 'Trittau', 'Großhansdorf', 'Ammersbek', 'Stapelfeld', 'Siek', 'Barsbüttel', 'Oststeinbek', 'Braak', 'Hamburg-Rahlstedt', 'Hamburg-Wandsbek', 'Hamburg-Tonndorf', 'Hoisdorf', 'Lütjensee'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium hover:border-brand-green/50 transition-colors">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten in Ahrensburg beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Kreis Stormarn • Termine meist in 24-48 Stunden</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-ahrensburg" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenAhrensburgPage;
